import React, { useState, useEffect, useCallback } from 'react';
import { Star, Send, X, Loader2, MessageSquare } from 'lucide-react';
import { getAuthToken, isLoggedIn as checkIsLoggedIn } from '../lib/tokenStorage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Star rating input
const StarInput = ({ value, onChange }) => (
  <div className="flex gap-1" data-testid="star-rating-input">
    {[1, 2, 3, 4, 5].map(n => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className="p-0.5 transition-transform hover:scale-110"
        data-testid={`star-${n}`}
      >
        <Star
          className={`w-5 h-5 transition-colors ${n <= value ? 'fill-[#e8ff00] text-[#e8ff00]' : 'text-zinc-700'}`}
        />
      </button>
    ))}
  </div>
);

// Individual review card
const ReviewCard = ({ review }) => {
  const date = new Date(review.created_at);
  const timeAgo = getTimeAgo(date);

  return (
    <div className="border-b border-zinc-800/50 py-3 last:border-0" data-testid="review-card">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-zinc-800 border border-zinc-700 flex items-center justify-center text-[10px] font-heading text-zinc-400">
            {(review.user_name || 'A')[0].toUpperCase()}
          </div>
          <span className="text-zinc-300 text-xs font-heading">{review.user_name}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="flex">
            {[1, 2, 3, 4, 5].map(n => (
              <Star key={n} className={`w-3 h-3 ${n <= review.rating ? 'fill-[#e8ff00] text-[#e8ff00]' : 'text-zinc-800'}`} />
            ))}
          </div>
          <span className="text-zinc-600 text-[10px]">{timeAgo}</span>
        </div>
      </div>
      <p className="text-zinc-400 text-xs leading-relaxed">{review.text}</p>
    </div>
  );
};

function getTimeAgo(date) {
  const seconds = Math.floor((new Date() - date) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

const GymReviewPanel = ({ gym, isOpen, onClose }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rating, setRating] = useState(0);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const fetchReviews = useCallback(async () => {
    if (!isOpen) return;
    setLoading(true);
    try {
      const encodedName = encodeURIComponent(gym.name);
      const encodedCity = encodeURIComponent(gym.city);
      const encodedState = encodeURIComponent(gym.state);
      const res = await fetch(`${API_URL}/api/gym-reviews/${encodedName}/${encodedCity}/${encodedState}`);
      if (res.ok) {
        const data = await res.json();
        setReviews(data);
      }
    } catch {
      // silent fail
    }
    setLoading(false);
  }, [gym, isOpen]);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) { setError('Please select a star rating'); return; }
    if (text.length < 10) { setError('Review must be at least 10 characters'); return; }
    setError('');
    setSubmitting(true);

    const token = getAuthToken();
    if (!token) { setError('Please log in to submit a review'); setSubmitting(false); return; }

    try {
      const res = await fetch(`${API_URL}/api/gym-reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          gym_name: gym.name,
          gym_city: gym.city,
          gym_state: gym.state,
          rating,
          text,
        }),
      });
      if (res.ok) {
        setSuccess(true);
        setRating(0);
        setText('');
        fetchReviews();
        setTimeout(() => setSuccess(false), 3000);
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to submit review');
      }
    } catch {
      setError('Network error. Please try again.');
    }
    setSubmitting(false);
  };

  if (!isOpen) return null;

  const isLoggedIn = checkIsLoggedIn();
  const avgRating = reviews.length > 0
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  return (
    <div className="mt-3 border-t border-zinc-800/50 pt-3" data-testid="gym-review-panel">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-3.5 h-3.5 text-[#e8ff00]" />
          <span className="text-white text-xs font-heading">
            Reviews {reviews.length > 0 && <span className="text-zinc-500">({reviews.length})</span>}
          </span>
          {avgRating && (
            <span className="flex items-center gap-1 text-[#e8ff00] text-xs">
              <Star className="w-3 h-3 fill-[#e8ff00]" /> {avgRating}
            </span>
          )}
        </div>
        <button onClick={onClose} className="text-zinc-600 hover:text-zinc-400 transition-colors" data-testid="close-reviews">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Reviews list */}
      {/* eslint-disable-next-line no-nested-ternary -- readable tier picker */}
      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-4 h-4 text-zinc-600 animate-spin" />
        </div>
      ) : reviews.length > 0 ? (
        <div className="max-h-48 overflow-y-auto mb-3 scrollbar-hide">
          {reviews.map((r, i) => <ReviewCard key={r.review_id || i} review={r} />)}
        </div>
      ) : (
        <p className="text-zinc-600 text-xs text-center py-3 mb-3">No reviews yet. Be the first!</p>
      )}

      {/* Review form */}
      {isLoggedIn ? (
        <form onSubmit={handleSubmit} className="space-y-2" data-testid="review-form">
          <div className="flex items-center gap-3">
            <StarInput value={rating} onChange={setRating} />
            {success && <span className="text-green-500 text-[10px] font-heading">Review submitted!</span>}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="Share your experience training here..."
              className="flex-1 bg-zinc-800 border border-zinc-700 text-white text-xs px-3 py-2 placeholder-zinc-600 focus:border-[#e8ff00] focus:outline-none transition-colors"
              maxLength={500}
              data-testid="review-text-input"
            />
            <button
              type="submit"
              disabled={submitting}
              className="flex items-center gap-1.5 px-3 py-2 bg-[#e8ff00] text-black text-xs font-heading uppercase tracking-wider hover:opacity-90 transition-opacity disabled:opacity-50"
              data-testid="review-submit-btn"
            >
              {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            </button>
          </div>
          {error && <p className="text-red-400 text-[10px]" data-testid="review-error">{error}</p>}
        </form>
      ) : (
        <p className="text-zinc-500 text-xs text-center py-2 bg-zinc-900 border border-zinc-800" data-testid="login-prompt">
          <a href="/auth" className="text-[#e8ff00] hover:underline">Log in</a> to leave a review
        </p>
      )}
    </div>
  );
};

// Hook to fetch all review stats for the gym finder
export function useGymReviewStats() {
  const [stats, setStats] = useState({});

  useEffect(() => {
    fetch(`${API_URL}/api/gym-reviews/stats`)
      .then(r => r.ok ? r.json() : {})
      .then(data => setStats(data))
      .catch(() => {});
  }, []);

  return stats;
}

export default GymReviewPanel;
